<template>
  <div>
    <ExitNavBar :title="getNavTitle" v-on:exit="exit()" />
    <div class="add-edit-case">
      <!-- id is used to calculate sticky position of multiple sticky elements -->
      <div class="navbar-background" id="addEditNavBar">
        <div v-if="isEditable" class="add-edit-case-nav-bar" data-cy="case-nav-bar">
          <h3 class="page-title" data-cy="case-title">
            {{ getPageTitle() }}
          </h3>
          <BreadcrumbMenu :key="refreshBreadCrumbMenu" :crumbs="caseBreadcrumbMenuItems"> </BreadcrumbMenu>
          <div class="action-buttons">
            <BackstageButton
              :disabled="!currentStepActionIsEnabled"
              :variant="ButtonVariant.PRIMARY"
              :icon-right="saving ? 'loading' : 'arrow-right'"
              :data-cy="currentStep + '-button-cnt'"
              :data-cy-button="currentStep + '-button'"
              v-on:click="executeCurrentStepAction()">
              {{ $t(`create_edit_case.execute_${currentStep}_step_button`) }}
            </BackstageButton>
          </div>
        </div>
      </div>
      <div v-if="showCurrentStepContent" class="current-step-content">
        <ProductsModelsSelector
          v-if="currentStep === 'products-selection'"
          :order-type="caseType"
          :account-id="accountId"
          :order-date="formatDate(getCaseDate())"
          :pre-selected-product-model-names="allSelectedProductModels"
          v-on:selected-product-models="setSelectedProductModels">
        </ProductsModelsSelector>
        <ProductsQuantitiesSelector
          v-if="currentStep === 'quantities'"
          :matrix-type="matrixType"
          :account-id="account.id"
          :default-size="size"
          :selected-product-models="selectedProductModels"
          :matrix-orders="matrixOrders"
          :submitting="saving"
          :minimum-date="getCaseDate()"
          :is-existing-order="isExistingCase"
          @update:matrixOrders="updateMatrixOrders"
          v-on:all-orders-are-valid="updateAllOrdersAreValid">
        </ProductsQuantitiesSelector>
        <CaseView
          v-if="currentStep === 'details'"
          :id="id"
          :case-type="caseType"
          v-on:submit-ready="onSubmitReady"
          v-on:not-submitted="notSubmittedEvent"
          v-on:cancel="exit()"
          v-on:delete-line="updateMatrix"
          v-on:can-leave-page="
            () => {
              canLeavePage = true;
            }
          ">
        </CaseView>
        <!-- ToDo remove as soon as we check user are ok with not having this here -->
        <!-- <div class="buttons-container">
          <BackstageButton
            v-if="caseNotSubmitted"
            variant="secondary"
            data-cy="edit-button"
            v-on:click="edit()">
            {{ $t('global.edit_button') }}
          </BackstageButton>
          <BackstageButton variant="secondary" data-cy="cancel-button" v-on:click="exit()">
            {{ $t('global.cancel_button') }}
          </BackstageButton>
          <BackstageButton
            v-if="caseNotSubmitted"
            data-cy="submit-button"
            :class="[{ submitting: saving }]"
            :disabled="!isSubmitReady"
            v-on:click="submit()">
            {{ $t('viewCase.submit_button') }}
          </BackstageButton>
        </div> -->
      </div>
      <div v-else class="loader"></div>
      <div v-if="caseNotSubmitted && showSubmitPopup">
        <NotificationMessage
          :title="$t('viewCase.submit_reminder_popup_title')"
          :size="popUpSize"
          :show-close-button="true"
          v-on:close="closePopup">
          <SubmitReminder
            :submitting="saving"
            :deleting="deleting"
            :submit-enabled="isSubmitReady"
            v-on:save="goBackToCases"
            v-on:delete="deleteCase"
            v-on:submit="submit" />
        </NotificationMessage>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { globalConstants } from '@/constants/global.constants';
import { accountMixins } from '@/mixins/account.mixins';
import { utilsMixins } from '@/mixins/utils.mixins';
import { AddEditCaseBreadcrumbSteps } from '@/models/add-edit-case-breadcrumb-steps.enum';
import { type BreadcrumbItem } from '@/models/breadcrumb-item';
import { CaseLine } from '@/models/case-line.model';
import { Case } from '@/models/case.model';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { MatrixOrder } from '@/models/matrix-order.model';
import { MatrixProduct } from '@/models/matrix-product.model';
import { MatrixType } from '@/models/matrix-type.enum';
import { ProductMainGroups } from '@/models/product-main-group.enum';
import { returnRequestTypeByLegalEntity } from '@/models/return-request-type-by-region.model';
import { CasesService } from '@/services/cases.service';
import { CaseReturnRequestType, CaseStatus, CaseType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { mappersMixins } from '@/mixins/mappers.mixins';
import { useUserStore } from '@/stores/user';

import NotificationMessage from '@/components/common/NotificationMessage.vue';
import { useLocale } from '@/composables/locale';
import { useAddEditCaseStore } from '@/stores/add-edit-case';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { PopUpSize } from '@/models/pop-up-size.enum';
import CaseView from '../views/CaseView.vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import ExitNavBar from '@/components/common/exit-nav-bar.vue';
import ProductsQuantitiesSelector from '@/components/matrix/products-quantities-selector.vue';
import ProductsModelsSelector from '@/components/matrix/products-models-selector.vue';
import SubmitReminder from '@/components/common/submit-reminder.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useFormat } from '../composables/format';
import { ButtonVariant } from '@/models/button-variant.enum';
import { useAnalytics } from '@/composables/analytics';

export default defineComponent({
  name: 'create-edit-case',
  components: {
    ProductsModelsSelector,
    ProductsQuantitiesSelector,
    CaseView,
    BreadcrumbMenu,
    ExitNavBar,
    SubmitReminder,
    NotificationMessage,
    BackstageButton,
  },
  beforeRouteLeave(to, _, next) {
    if (this.canLeavePage || !!to.params['step'] || to.path === '/') {
      return next();
    }

    this.confirmNavigatingAway(to.path);
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    caseType: {
      type: String,
      default: undefined,
    },
  },
  setup() {
    const { selectedAccount, selectedAccountId } = storeToRefs(useUserStore());
    const { editingProductModels, editingMatrixOrders, editingCases } = storeToRefs(useAddEditCaseStore());
    const route = useRoute();
    const router = useRouter();
    const { isSalesRep } = useUserStore();
    const {
      addEditingProductModels,
      removeEditingProductModels,
      addEditingMatrixOrders,
      removeEditingMatrixOrders,
      addEditingCases,
      removeEditingCases,
      $reset,
    } = useAddEditCaseStore();
    const { formatDate } = useFormat();
    const { addCustomGaEvent } = useAnalytics();

    return {
      accountId: selectedAccountId,
      account: selectedAccount,
      editingProductModels,
      editingMatrixOrders,
      editingCases,
      route,
      router,
      ButtonVariant,
      addCustomGaEvent,
      addEditingProductModels,
      removeEditingProductModels,
      addEditingMatrixOrders,
      removeEditingMatrixOrders,
      addEditingCases,
      removeEditingCases,
      resetAddEditStore: $reset,
      isSalesRep,
      formatDate,
    };
  },
  data() {
    return {
      savingQueue: [] as (() => Promise<any>)[],
      casesService: new CasesService(),
      saving: false,
      isSubmitReady: false,
      matrixType: '',
      locationName: '',
      size: undefined as unknown as string,
      matrixOrders: new Array<MatrixOrder>(),
      caseOrder: undefined as unknown as Case,
      refreshBreadCrumbMenu: 0,
      allQuantitiesAreValid: false,
      showCurrentStepContent: false,
      caseNotSubmitted: false,
      canLeavePage: false,
      showSubmitPopup: false,
      deleting: false,
      queued: false,
      popUpSize: PopUpSize.Small,
      currentStep: AddEditCaseBreadcrumbSteps.productsModelsSelection,
      selectedProductModels: new Array<string>(),
    };
  },
  computed: {
    getNavTitle(): string {
      const caseNumber = this.caseOrder?.caseNumber ? ` - ${this.caseOrder?.caseNumber}` : '';
      return (
        this.$t(`create_edit_case.case_type['${(this.caseType as CaseType).toLowerCase()}']`).toString() + caseNumber
      );
    },
    caseBreadcrumbMenuItems(): BreadcrumbItem[] {
      const items = [
        this.getCaseBreadcrumbMenuItem(AddEditCaseBreadcrumbSteps.productsModelsSelection),
        this.getCaseBreadcrumbMenuItem(AddEditCaseBreadcrumbSteps.productsQuantities),
        this.getCaseBreadcrumbMenuItem(AddEditCaseBreadcrumbSteps.details),
      ];

      return items;
    },
    currentStepActionIsEnabled(): boolean {
      if (this.saving) {
        return false;
      }

      switch (this.currentStep) {
        case AddEditCaseBreadcrumbSteps.productsModelsSelection:
          return this.editingProductModels.length > 0;
        case AddEditCaseBreadcrumbSteps.productsQuantities:
          return this.allQuantitiesAreValid && this.matrixOrders[0]?.matrixProducts?.length > 0;
        case AddEditCaseBreadcrumbSteps.details:
          return this.isSubmitReady;
        default:
          return true;
      }
    },
    isEditable(): boolean {
      return this.isNotSubmittedCase || !this.isExistingCase || this.caseNotSubmitted;
    },
    allSelectedProductModels(): string[] {
      return this.selectedProductModels;
    },
    isExistingCase(): boolean {
      return !!(this.id || this.caseOrder?.id);
    },
    isNotSubmittedCase(): boolean {
      return this.caseOrder && this.caseOrder.status === CaseStatus.NotSubmitted;
    },
    isWarranty(): boolean {
      return this.caseType === CaseType.Warranty;
    },
    isReturn(): boolean {
      return this.caseType === CaseType.Return;
    },
  },
  watch: {
    $route(to) {
      this.canLeavePage = false;

      if (this.currentStep === AddEditCaseBreadcrumbSteps.productsQuantities) {
        this.addEditingMatrixOrders(this.matrixOrders);
      }

      this.currentStep = to.params['step'] ?? AddEditCaseBreadcrumbSteps.productsModelsSelection;

      if (this.currentStep === AddEditCaseBreadcrumbSteps.details) {
        this.canLeavePage = true;
      }

      this.refreshBreadCrumbMenu++;
    },
    account: function (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.showCurrentStepContent = false;
        this.caseCreationEnabled();
        this.resetAddEditStore();
        this.canLeavePage = true;
        const goToCases = this.caseType ? `/${this.caseType.toLocaleLowerCase()}-cases` : '/';

        this.$router.push(goToCases);
      }
    },
  },
  beforeMount() {
    this.resetAddEditStore();
    this.selectedProductModels = this.editingProductModels;
    this.matrixOrders = this.editingMatrixOrders;
    this.caseOrder = this.editingCases[0];
  },
  async mounted() {
    this.caseCreationEnabled();

    this.editingProductModels = this.editingProductModels ?? ([] as string[]);
    const caseOrder = (this.editingCases ?? []) as Case[];

    if (!caseOrder?.length) {
      this.caseOrder = caseOrder[0];
    }

    await this.configureMatrix();
    this.updateSelectedProductsModels();
    this.setInitialStep();

    this.showCurrentStepContent = true;
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnloadHandler);
  },
  methods: {
    ...utilsMixins,
    ...accountMixins,
    ...mappersMixins,
    onBeforeUnloadHandler(event: BeforeUnloadEvent) {
      if (this.currentStep === AddEditCaseBreadcrumbSteps.productsQuantities) {
        this.addEditingMatrixOrders(this.matrixOrders);
      } else if (this.currentStep === AddEditCaseBreadcrumbSteps.details) {
        this.addEditingCases([this.caseOrder]);
      }

      event.preventDefault();
      event.returnValue = '';
    },
    getPageTitle() {
      const titlePrefix = this.isEditable ? `${this.$t('viewCase.page_title_new_prefix')} ` : '';

      return titlePrefix + this.$t(`viewCase.page_title_${this.caseType?.toLowerCase()}`);
    },
    onSubmitReady({ isValidCase, caseOrder }: { isValidCase: boolean; caseOrder: Case }) {
      this.isSubmitReady = isValidCase;

      if (this.isSubmitReady) {
        this.caseOrder = caseOrder;
      }
    },
    async exit() {
      if (
        this.currentStep === AddEditCaseBreadcrumbSteps.productsQuantities &&
        (this.matrixOrders?.[0]?.total ?? 0) > 0
      ) {
        this.canLeavePage = true;
        this.confirmNavigatingAway(`/${(this.caseType as CaseType).toLocaleLowerCase()}-cases`);
      } else if (
        this.currentStep !== AddEditCaseBreadcrumbSteps.productsModelsSelection &&
        this.isExistingCase &&
        this.isEditable
      ) {
        this.showSubmitPopup = true;
      } else {
        this.canLeavePage = true;
        this.router.push(`/${(this.caseType as CaseType).toLocaleLowerCase()}-cases`);
      }
    },
    closePopup() {
      this.showSubmitPopup = false;
    },
    confirmNavigatingAway(path: string) {
      this.$snotify.error(this.$t('global.warning_unsaved_changes'), {
        buttons: [
          {
            text: `${this.$t('global.continue_button')}`,
            action: () => {
              this.canLeavePage = true;
              this.$router.push(path);
            },
            className: ButtonVariant.SECONDARY,
          },
        ],
      });
    },
    showErrorMessage(errorMessage: string[]) {
      this.$snotify.error(this.$t(`${errorMessage}`));
    },
    notSubmittedEvent(caseNotSubmitted_: boolean) {
      this.caseNotSubmitted = caseNotSubmitted_;
      this.canLeavePage = !this.caseNotSubmitted;
    },
    getCaseDate(): Date {
      return this.caseOrder?.createdAt ? new Date(this.caseOrder.createdAt) : new Date();
    },
    async caseCreationEnabled() {
      if (!(await this.accountHasCaseCreationEnabled(this.caseType as CaseType, this.accountId))) {
        this.canLeavePage = true;
        this.exit();

        return;
      }
    },
    async configureMatrix() {
      this.matrixType = this.caseType === CaseType.Warranty ? MatrixType.warranty : MatrixType.return;
      const defaultSize = this.$t(`config.default_size_run['${this.account.billingCountry}']`);

      if (globalConstants.sizeTypes.includes(defaultSize)) {
        this.size = defaultSize;
      }

      await this.initializeOrUpdateMatrix(this.id || this.caseOrder?.id);
    },
    mapCaseLineToMatrixProduct(line: CaseLine): MatrixProduct {
      const initialProduct = new MatrixProduct();

      initialProduct.productId = line.productId;
      initialProduct.productGroupId = line.productGroupId;
      initialProduct.productGroupName = line.productGroupName;
      initialProduct.productGroupNumber = line.productNumber;
      initialProduct.picUrl = line.productImageUrl;
      initialProduct.sizeId = line.productFabricSize.split(' ').pop() ?? '';
      initialProduct.color = line.productFabricSize.replace(initialProduct.sizeId, '');
      initialProduct.mainGroup = line.productMainGroup;
      initialProduct.total = line.quantity ?? 1;

      return initialProduct;
    },
    mapCaseLinesToMatrixProducts() {
      if (this.caseOrder.lines?.length > 0) {
        if (this.editingProductModels?.length) {
          this.filterCaseOrderLinesOnSelectedModels();
        }

        this.caseOrder.lines.forEach((line: CaseLine) => {
          let initialProduct = this.matrixOrders[0].matrixProducts.find(
            (product: MatrixProduct) => product.productId === line.productId,
          );

          if (initialProduct) {
            return;
          }

          initialProduct = this.mapCaseLineToMatrixProduct(line);

          this.matrixOrders[0]?.matrixProducts.push(initialProduct);
        });
      }
    },
    async initializeOrUpdateMatrix(id?: string) {
      const editingMatrixOrders = (this.editingMatrixOrders ?? []) as MatrixOrder[];

      if (id || this.caseOrder?.id) {
        const caseValidation = await this.casesService.get(id || this.caseOrder.id, this.account.id);

        if (caseValidation.model) {
          this.caseOrder = caseValidation.model as unknown as Case;

          if (editingMatrixOrders[0]?.matrixProducts?.length) {
            this.matrixOrders = editingMatrixOrders;
            this.updateMatrix();
            this.filterCaseOrderLinesOnSelectedModels();
          } else {
            this.matrixOrders = new Array<MatrixOrder>();
            const matrixOrder = {
              matrixProducts: new Array<MatrixProduct>(),
            } as MatrixOrder;

            this.matrixOrders.push(matrixOrder);
          }

          this.matrixOrders[0].date = this.getCaseDate();

          this.matrixType =
            (this.caseOrder?.type || this.caseType) === CaseType.Warranty ? MatrixType.warranty : MatrixType.return;

          this.mapCaseLinesToMatrixProducts();

          this.addEditingCases([this.caseOrder]);
        } else {
          // ToDo show error
          this.exit();
        }
      } else {
        this.matrixOrders =
          editingMatrixOrders.length === 0
            ? [
                {
                  date: new Date(),
                  selected: true,
                  matrixProducts: [],
                  total: 0,
                },
              ]
            : editingMatrixOrders;
      }
    },
    updateSelectedProductsModels() {
      const caseProducts: string[] = this.matrixOrders[0]?.matrixProducts?.reduce(
        (productGroupNames: string[], product: MatrixProduct) =>
          productGroupNames.concat(product?.productGroupName) ?? [],
        [],
      );
      const allSelectedProductModels = new Set<string>([
        ...this.editingProductModels,
        ...(caseProducts ? caseProducts : []),
      ]);

      this.addEditingProductModels(Array.from(allSelectedProductModels));
      this.selectedProductModels = this.editingProductModels;
    },
    updateMatrix(productId = '') {
      this.filterProductsBasedOnSelectedModels();

      if (productId) {
        this.filterMatrixProductsBasedOnCaseOrderProducts(productId);
      }
      this.addEditingMatrixOrders(this.matrixOrders);
    },
    filterProductsBasedOnSelectedModels() {
      if (this.matrixOrders[0]) {
        this.matrixOrders[0].matrixProducts = this.matrixOrders[0]?.matrixProducts.filter((product: MatrixProduct) =>
          this.editingProductModels.includes(product.productGroupId),
        );
        this.matrixOrders[0].total = this.matrixOrders[0]?.matrixProducts.reduce(
          (matrixTotal: number, product: MatrixProduct) => matrixTotal + (product.total || 0),
          0,
        );
      }
    },
    filterMatrixProductsBasedOnCaseOrderProducts(productId: string) {
      this.matrixOrders[0]?.matrixProducts.forEach((product: MatrixProduct) => {
        if (product.productId === productId) {
          product.total--;
        }
      });
      this.matrixOrders[0].total = this.matrixOrders[0]?.matrixProducts.reduce(
        (matrixTotal: number, product: MatrixProduct) => matrixTotal + (product.total || 0),
        0,
      );
    },
    filterCaseOrderLinesOnSelectedModels() {
      if (this.caseOrder?.lines) {
        this.caseOrder.lines =
          this.caseOrder?.lines.filter((line: CaseLine) => this.editingProductModels.includes(line.productGroupId)) ||
          [];
        this.addEditingCases([this.caseOrder]);
      }
    },
    setInitialStep() {
      const urlStep = this.$route.params['step'] as AddEditCaseBreadcrumbSteps;

      if (urlStep === AddEditCaseBreadcrumbSteps.productsQuantities && this.allSelectedProductModels.length === 0) {
        return this.$router.replace({
          params: {
            ...this.$route.params,
            step: AddEditCaseBreadcrumbSteps.productsModelsSelection,
          },
        });
      }

      this.currentStep = urlStep;
    },
    async upsertCase(matrixOrders: MatrixOrder[]) {
      if (this.saving) {
        return;
      }

      this.saving = true;

      const caseOrder = (this.editingCases ?? []) as Case[];

      if (!caseOrder?.length) {
        this.caseOrder = new Case();
        this.caseOrder.accountId = this.accountId;
        this.caseOrder.type = this.caseType as CaseType;
      } else {
        this.caseOrder = caseOrder[0];
        this.filterCaseOrderLinesOnSelectedModels();
      }

      if (this.caseType === CaseType.Warranty) {
        this.caseOrder.lines = this.warrantyCaseLines(matrixOrders);
      } else {
        this.caseOrder.lines = this.returnCaseLines(matrixOrders);

        if (this.didReturnRequestTypeChange()) {
          this.unsetReturnFields();
        }
      }

      // ToDo strangely result seems to be returning before awaiting for a result
      const result = await (this.id || this.caseOrder.id
        ? this.casesService.update(this.id || this.caseOrder.id, this.caseOrder, this.accountId)
        : this.casesService.create(this.caseOrder, this.accountId));

      if (result.isValid()) {
        if (result.model) {
          this.caseOrder = result.model as unknown as Case;

          this.addEditingMatrixOrders(this.matrixOrders);
          this.addEditingCases([this.caseOrder]);
        }

        const caseId = this.caseOrder.id || result.model?.id;

        this.$router.push(
          `/${(this.caseType as CaseType).toLocaleLowerCase()}-cases/${caseId}/${AddEditCaseBreadcrumbSteps.details}`,
        );
      } else {
        // ToDo show error messages
        this.showErrorMessage(result.errors.map((error) => error.error));
      }

      this.saving = false;
    },
    async submit() {
      try {
        // if the case is not ready to submit/not valid (may be we need to change isSubmitReady to isValidCase)
        if (!this.isSubmitReady) {
          return;
        }

        this.saving = true;
        let isValidReturn = false;

        // for now we only validate return cases
        if (this.isReturn) {
          const validationResult = await this.casesService.validate(
            this.caseOrder.id,
            this.mapCaseUpdate(this.caseOrder, this.isPickupRequest()),
            this.account.id,
          );

          isValidReturn = validationResult.isValid();

          if (!isValidReturn) {
            console.error({ msg: 'Return case validation failed', caseId: this.caseOrder.id });
            this.saving = false;

            return;
          }
        }

        const submitResult = await this.casesService.submit(this.id || this.caseOrder?.id, this.account.id);

        if (submitResult.isValid()) {
          this.$snotify.success(this.$t(`viewCase.submit_${(this.caseType as CaseType).toLowerCase()}_success`));
          this.canLeavePage = true;
          this.goBackToCases();

          this.addCustomGaEvent({
            event: GaCustomEvents.SubmitReturn,
            return_type: (this.caseType as CaseType).toLowerCase(),
          });
          this.addEditingCases([this.caseOrder]);
        }
      } catch (err) {
        console.error(err);
      }

      this.saving = false;
    },
    warrantyCaseLines(matrixOrders: MatrixOrder[]): CaseLine[] {
      const lines = new Array<CaseLine>();

      matrixOrders[0]?.matrixProducts.forEach((matrixProduct) => {
        const maximumQuantityPerWarrantyLine = 1;

        for (let i = 0; i < matrixProduct.total; i++) {
          let line = new CaseLine();
          let lineFound = false;

          if (this.caseOrder?.lines) {
            this.caseOrder.lines = this.caseOrder.lines?.filter((caseLine: CaseLine) => {
              if (!lineFound && caseLine.productId === matrixProduct.productId) {
                line = caseLine;
                lineFound = true;

                return false;
              }

              return true;
            });
          }

          line = { ...line, ...this.mapMatrixToCaseLine(matrixProduct) };
          line.quantity = maximumQuantityPerWarrantyLine;
          lines.push(line);
        }
      });

      return lines;
    },
    returnCaseLines(matrixOrders: MatrixOrder[]): CaseLine[] {
      return matrixOrders[0]?.matrixProducts
        ?.filter((product) => product.total !== 0)
        ?.map((product) => {
          const line = this.mapMatrixToCaseLine(product);

          line.quantity = product.total;

          return line;
        });
    },
    mapMatrixToCaseLine(matrixProduct: MatrixProduct): CaseLine {
      const line = new CaseLine();

      line.productId = matrixProduct.productId;
      line.productGroupId = matrixProduct.productGroupId;
      line.productMainGroup = matrixProduct.mainGroup;
      line.productName = matrixProduct.productName;
      line.productNumber = matrixProduct.productGroupNumber;
      line.productFabricSize = matrixProduct.color + ' ' + matrixProduct.sizeId;
      line.productGroupName = matrixProduct.productGroupName;
      line.productImageUrl = this.getBoxImageProxyUrl(matrixProduct.picUrl);

      return line;
    },
    didReturnRequestTypeChange() {
      const isReturnPickupRequest = this.isPickupRequest();

      return (
        (isReturnPickupRequest && this.caseOrder.requestType === CaseReturnRequestType.Label) ||
        (!isReturnPickupRequest && this.caseOrder.requestType === CaseReturnRequestType.Pickup)
      );
    },
    isPickupRequest(): boolean {
      if (this.caseOrder.lines) {
        const totalShoeCount = this.caseOrder.lines.reduce((total: number, line: CaseLine) => {
          if (line.productMainGroup === ProductMainGroups.shoes) {
            return total + (line.quantity || 0);
          }

          return total;
        }, 0);
        const returnRequestType =
          returnRequestTypeByLegalEntity[this.account?.legalEntity.toLowerCase()] ??
          returnRequestTypeByLegalEntity.default;

        return (
          this.caseType === CaseType.Return &&
          returnRequestType.requestType === CaseReturnRequestType.Pickup &&
          totalShoeCount > returnRequestType.min
        );
      }

      return false;
    },
    getCaseBreadcrumbMenuItem(caseBreadcrumbItem: AddEditCaseBreadcrumbSteps): BreadcrumbItem {
      return {
        id: caseBreadcrumbItem,
        name: this.$t(`create_edit_case.breadcrumb_menu.${caseBreadcrumbItem}`).toString(),
        routerPath: `/${(this.caseType as CaseType).toLowerCase()}-cases/${
          this.id
            ? // update draft
              `${this.id}/`
            : // draft/s creation
              'create/'
        }${caseBreadcrumbItem}`,
      };
    },
    async executeCurrentStepAction() {
      switch (this.currentStep) {
        case AddEditCaseBreadcrumbSteps.productsModelsSelection:
          // When user fills the final form and then gets back to the product selection page,
          // the products selection step should get the updated case
          await this.initializeOrUpdateMatrix(this.route.params.id as string);

          // ToDo: should we avoid this for warranties and let the user manually delete the lines?
          if (this.caseType !== CaseType.Warranty) {
            this.filterProductsBasedOnSelectedModels();
          }

          return this.router.replace({
            params: { ...this.route.params, step: AddEditCaseBreadcrumbSteps.productsQuantities },
          });
        case AddEditCaseBreadcrumbSteps.productsQuantities:
          this.addEditingMatrixOrders(this.matrixOrders);
          // this.matrixOrders = this.editingMatrixOrders;
          this.upsertCase(this.matrixOrders);
          break;
        case AddEditCaseBreadcrumbSteps.details:
          return this.submit();
      }
    },
    setSelectedProductModels(selectedProductModels: string[]) {
      this.addEditingProductModels(selectedProductModels);
      this.selectedProductModels = selectedProductModels;
      this.updateMatrix();
      this.filterCaseOrderLinesOnSelectedModels();
    },
    updateAllOrdersAreValid(allQuantitiesAreValid: boolean) {
      this.allQuantitiesAreValid = allQuantitiesAreValid;
    },
    unsetReturnFields() {
      this.caseOrder.parcelCount = undefined;
      this.caseOrder.palletCount = undefined;
      this.caseOrder.pickup = undefined;
      this.caseOrder.requestType = CaseReturnRequestType.Label;
    },
    async deleteCase() {
      if (this.deleting) {
        return;
      }

      this.deleting = true;

      const response = await this.casesService.remove(this.caseOrder.id, this.account.id);

      if (response.isValid()) {
        this.goBackToCases();
      }

      this.deleting = false;
    },
    goBackToCases() {
      this.canLeavePage = true;
      this.$router.push(`/${(this.caseType as CaseType).toLowerCase()}-cases`);
    },
    edit() {
      this.$router.push(
        `/${(this.caseType as CaseType).toLowerCase()}-cases/${this.id}/${
          AddEditCaseBreadcrumbSteps.productsModelsSelection
        }`,
      );
    },
    updateMatrixOrders(newOrders: MatrixOrder[]) {
      this.matrixOrders = newOrders;
    },
  },
});
</script>

<style lang="scss" scoped>
.add-edit-case {
  margin-bottom: 1.25rem;

  .navbar-background {
    @include navbar-background;
  }

  .add-edit-case-nav-bar {
    @include common-container;
    padding: 20px;
    background-color: var(--primary-bg-color);
    padding: 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    border-radius: 0;
    gap: 30px;

    @include breakpoint(medium) {
      padding: 10px !important;
    }

    .page-title {
      font-size: 1.25rem;
      font-weight: 400;
      text-align: left;
    }

    .action-buttons {
      min-width: 10rem;
      .hidden {
        display: none;
      }
    }
  }

  .buttons-container {
    padding: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.5625rem;
    margin-bottom: 1rem;
  }
}
</style>
